import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../i18n';
import './Modal.scss';

/**
 * Generic modal component that supports:
 * - Generic mode: Renders custom children with title and close button
 * - Confirm/Alert mode: Pre-built confirm/alert dialogs
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {string} props.title - Modal title
 * 
 * Generic mode props:
 * @param {React.ReactNode} [props.children] - Custom content for generic mode
 * @param {number} [props.maxWidth=440] - Maximum width in pixels
 * @param {boolean} [props.showCloseButton=true] - Show close button in header
 * 
 * Confirm/Alert mode props:
 * @param {'confirm'|'alert'} [props.mode] - Dialog mode (if set, uses confirm/alert mode)
 * @param {string} [props.message] - Dialog message (confirm/alert mode)
 * @param {'default'|'danger'|'success'|'error'} [props.variant='default'] - Visual variant
 * @param {string} [props.confirmLabel] - Confirm button label
 * @param {string} [props.cancelLabel='Cancel'] - Cancel button label (confirm mode only)
 * @param {Function} [props.onConfirm] - Callback when confirmed (confirm/alert mode)
 * @param {Function} [props.onCancel] - Callback when cancelled (confirm mode only)
 */
function Modal({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 440,
  showCloseButton = true,
  // Confirm/Alert mode props
  mode,
  message,
  variant = 'default',
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel
}) {
  const { t } = useTranslation();
  const confirmButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const modalRef = useRef(null);

  // Determine if we're in confirm/alert mode or generic mode
  const isDialogMode = mode === 'confirm' || mode === 'alert';

  // Determine default confirm label based on mode
  const defaultConfirmLabel = mode === 'alert' ? t('common.ok') : t('common.confirm');
  const finalConfirmLabel = confirmLabel || defaultConfirmLabel;
  const finalCancelLabel = cancelLabel || t('common.cancel');

  // Focus management when modal opens
  useEffect(() => {
    if (!isOpen) return;

    if (isDialogMode) {
      // For danger variant in confirm mode, focus cancel button for safety
      // For all other cases, focus confirm/OK button
      if (mode === 'confirm' && variant === 'danger' && cancelButtonRef.current) {
        cancelButtonRef.current.focus();
      } else if (confirmButtonRef.current) {
        confirmButtonRef.current.focus();
      }
    } else {
      // Generic mode: focus first focusable element in modal
      if (modalRef.current) {
        const focusable = modalRef.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        focusable?.focus();
      }
    }
  }, [isOpen, mode, variant, isDialogMode]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isDialogMode) {
          if (mode === 'confirm' && onCancel) {
            onCancel();
          } else {
            onConfirm();
          }
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, mode, isDialogMode, onConfirm, onCancel, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = () => {
    if (isDialogMode) {
      if (mode === 'confirm' && onCancel) {
        onCancel();
      } else {
        onConfirm();
      }
    } else {
      onClose();
    }
  };

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  // Determine button class based on variant
  const confirmButtonClass = [
    'modal__btn',
    'modal__btn--confirm',
    variant === 'danger' ? 'modal__btn--danger' : '',
    variant === 'success' ? 'modal__btn--success' : '',
    variant === 'error' ? 'modal__btn--error' : ''
  ].filter(Boolean).join(' ');

  const modalClass = ['modal', isDialogMode ? 'modal--dialog' : ''].filter(Boolean).join(' ');

  const content = (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div 
        ref={modalRef}
        className={modalClass} 
        style={{ maxWidth: `${maxWidth}px` }}
        onClick={handleContentClick}
      >
        <div className="modal__header">
          <h3 className="modal__title">{title}</h3>
          {!isDialogMode && showCloseButton && (
            <button className="modal__close" onClick={onClose} aria-label={t('common.close')}>
              ×
            </button>
          )}
        </div>

        <div className="modal__content">
          {isDialogMode ? (
            <>
              <p className="modal__message">{message}</p>
              <div className="modal__actions">
                {mode === 'confirm' && (
                  <button
                    ref={cancelButtonRef}
                    className="modal__btn modal__btn--cancel"
                    onClick={onCancel}
                  >
                    {finalCancelLabel}
                  </button>
                )}
                <button
                  ref={confirmButtonRef}
                  className={confirmButtonClass}
                  onClick={onConfirm}
                >
                  {finalConfirmLabel}
                </button>
              </div>
            </>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export default Modal;
